import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText, Pencil, Mail, Eye } from 'lucide-react';
import { EmailTemplate } from '@/types/emailTemplates';

interface TemplateListProps {
  loading: boolean;
  templates: EmailTemplate[];
  filteredTemplates: EmailTemplate[];
  searchQuery: string;
  emailCategoryColors: Record<string, string>;
  onEditTemplate: (id: string) => void;
  onPreviewTemplate: (id: string) => void;
  onTestEmail: (templateId: string) => void;
  onResetFilters: () => void;
}

export function TemplateList({
  loading,
  templates,
  filteredTemplates,
  searchQuery,
  emailCategoryColors,
  onEditTemplate,
  onPreviewTemplate,
  onTestEmail,
  onResetFilters
}: TemplateListProps) {
  if (loading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="p-4 border rounded-md">
            <Skeleton className="h-6 w-36 mb-2" />
            <Skeleton className="h-4 w-64 mb-1" />
            <Skeleton className="h-4 w-full" />
            <div className="flex justify-between mt-4">
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-6 w-24" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  if (filteredTemplates.length === 0) {
    return (
      <div className="text-center p-8 border border-dashed rounded-md">
        <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-2 opacity-50" />
        <h3 className="text-lg font-medium mb-1">No templates found</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {searchQuery ? 'Try a different search query.' : 'No email templates match the selected filters.'}
        </p>
        <Button 
          variant="outline" 
          size="sm"
          onClick={onResetFilters}
        >
          Reset Filters
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {filteredTemplates.map((template) => (
        <div key={template.id} className="p-4 border rounded-md hover:shadow-sm transition-all">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-medium">{template.name}</h3>
            <Badge 
              className={emailCategoryColors[template.category] || 'bg-gray-500'}
            >
              {template.category}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mb-2">{template.description || 'No description provided.'}</p>
          <p className="text-xs text-muted-foreground mb-3">ID: {template.template_id} • Version: {template.version}</p>
          
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center gap-1"
                onClick={() => onEditTemplate(template.id)}
              >
                <Pencil className="h-3 w-3" />
                Edit
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center gap-1"
                onClick={() => onPreviewTemplate(template.id)}
              >
                <Eye className="h-3 w-3" />
                Preview
              </Button>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="flex items-center gap-1"
              onClick={() => onTestEmail(template.id)}
            >
              <Mail className="h-3 w-3" />
              Test
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
